import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAnimatedPlaceholder } from '@/hooks/use-animated-placeholder';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import SearchSuggestions from '@/components/search/SearchSuggestions';
import SearchInput from '@/components/search/SearchInput';

const PLACEHOLDER_WORDS = [
  'name',
  'location',
  'discipline',
  'profession'
];

const SearchBar = () => {
  const [searchValue, setSearchValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  
  // Don't show search on profile pages on mobile
  const isProfilePage = location.pathname.includes('/profile/');
  if (isMobile && isProfilePage) {
    // Return an empty div instead of null to prevent layout issues
    return <div className="w-full hidden md:block"></div>;
  }

  // Use our custom hooks
  const { currentWord } = useAnimatedPlaceholder({
    placeholderWords: PLACEHOLDER_WORDS,
    isFocused,
    hasValue: !!searchValue
  });
  
  const { suggestions, isLoading } = useSearchSuggestions(searchValue);

  // Handle click outside to close suggestions
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSuggestionClick = (id: string) => {
    navigate(`/u/${id}`);
    setSearchValue('');
    setIsFocused(false);
  };

  const clearSearch = () => {
    setSearchValue('');
    setIsFocused(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/?search=${encodeURIComponent(searchValue.trim())}`);
      setIsFocused(false);
    }
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSearch} className="w-full">
        <SearchInput
          ref={inputRef}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onClear={clearSearch}
          placeholder={isFocused ? "Search by name..." : `Search by ${currentWord}...`}
          isMobile={isMobile}
          isFocused={isFocused}
        />
      </form>
      
      {isFocused && (
        <SearchSuggestions
          suggestions={suggestions}
          isLoading={isLoading}
          searchValue={searchValue}
          onSuggestionClick={handleSuggestionClick}
          suggestionsRef={suggestionsRef}
        />
      )}
    </div>
  );
};

export default SearchBar;
